import { useEffect, useState, useCallback } from 'react';

export interface IPagingState {
  pageIndex: number;
  pageSize: number;
}

interface IPagingConfig {
  pageIndex?: number;
  pageSize?: number;
  onPageSizeChange?: (e: IPagingState) => void;
}

/**
 * 前端分页状态管理
 * 仅当 isOpenForegroundPage 为 true 时生效，维护 pageIndex / pageSize 并在数据变化时重置页码
 * @param isOpen 是否开启前端分页
 * @param paginationConfig 外部透传的分页配置（初始值 & 外部回调）
 * @param data 当前数据源，变化时自动重置到第 1 页
 */
export function useForegroundPaging(
  isOpen: boolean,
  paginationConfig: IPagingConfig | undefined,
  data: any[] | undefined,
) {
  const [pagingState, setPagingState] = useState<IPagingState>({
    pageIndex: paginationConfig?.pageIndex ?? 1,
    pageSize: paginationConfig?.pageSize ?? 10,
  });

  useEffect(() => {
    if (isOpen) {
      setPagingState(prev => ({ ...prev, pageIndex: 1 }));
    }
  }, [data, isOpen]);

  const handlePagingChange = useCallback(
    (e: { pageIndex?: number; pageSize?: number; }) => {
      setPagingState(prev => ({
        pageIndex: e.pageIndex ?? prev.pageIndex,
        pageSize: e.pageSize ?? prev.pageSize,
      }));
      paginationConfig?.onPageSizeChange?.(e as IPagingState);
    },
    [paginationConfig?.onPageSizeChange],
  );

  if (!isOpen) return null;

  return {
    pageIndex: pagingState.pageIndex,
    pageSize: pagingState.pageSize,
    onPageSizeChange: handlePagingChange,
  };
}
